import { observable } from "mobx";
import { IEmpresa, IRelatorios, newEmpresa, newIRelatorios } from "../../model";
import { api } from "../../api";
import { toast } from "react-toastify";
import { decodeUtf8String, downloadBinaryFile } from "../../utils";

export class _FaixaSimplesStore {
  obs = observable({
    open_solicitar: false as boolean,
    tasks_faixa_simples: [] as any[],
    selectedEmpresa: newEmpresa() as IEmpresa,
    modelo: "" as string,
    tipo: "" as string,
    relatorio: [] as any,
    length_tasks_faixa_simples: 0 as number,
    date_start: "" as string,
    date_end: "" as string,
    relatorios: [] as any,
    select_relatorio: newIRelatorios() as IRelatorios,
    columns: [] as any,
    loading: false as boolean,
    open_erros_relatorio: false as boolean,
    open_relatorios_salvos: false as boolean
  })

  /**
 * Solicita a criação de um novo relatório no backend com base nos parâmetros fornecidos.
 * 
 * A função envia uma requisição `POST` para o endpoint `/api/relatorio/task/create-faixa-simples`,
 * contendo os parâmetros de configuração do relatório.  
 * 
 * Em caso de sucesso:
 * - Exibe uma notificação de sucesso via `toast`.
 * - Redireciona o usuário para a página `/relatorios`.
 * 
 * Em caso de falha:
 * - Se o backend retornar um erro (`resp.data.erro`), exibe a mensagem correspondente.
 * - Caso ocorra uma exceção na requisição, exibe uma mensagem genérica de erro.
 * 
 * @async
 * @param {any} params Objeto contendo os parâmetros necessários para gerar o relatório.  
 * Recomenda-se definir uma interface específica (`SolicitarRelatorioParams`) para maior clareza e segurança.
 * 
 * @returns {Promise<void>} Retorna uma Promise resolvida após a solicitação e tratamento da resposta.
 */

  async handleSolicitarRelatorio(cnpj: string, razao_social: string) {

    const data = {
      cnpj: cnpj,
      razao_social: razao_social
    }

    try {

      let resp = await api.post("/api/relatorio/task/create-faixa-simples", data);

      if (resp.data.ok) {
        toast.success('Relatório Solicitado', {});
      } else {
        toast.error(resp.data.erro, {});
      }
    } catch (ex) {
      toast.error("Erro ao Solicitar Relatório, tente novamente mais tarde.", {});
    }
  }

  /**
 * Busca a lista paginada de relatórios solicitados.
 * 
 * A função envia uma requisição `POST` para o endpoint `/api/relatorio/task/index`,
 * informando o número da página (`page`) e um limite fixo de 15 registros por página.
 * 
 * Em caso de sucesso:
 * - Atualiza `tasks_relatorio` com a lista de itens retornados.
 * - Atualiza `length_tasks_relatorio` com a contagem total de registros.
 * 
 * Em caso de falha:
 * - Exibe uma mensagem de erro genérica via `toast`.
 * 
 * @async
 * @param {number} paged Número da página que deve ser carregada.
 * 
 * @returns {Promise<void>} Retorna uma Promise resolvida após a atualização da lista de relatórios.
 */

  async handleTasksPaged(paged: number, cnpj: string) {
    try {
      let data = {
        page: paged,
        limit: 15,
        cnpj: cnpj
      }

      let resp = await api.post("/api/relatorio/task/index-faixa-simples", data);

      this.obs.tasks_faixa_simples = [];
      this.obs.length_tasks_faixa_simples = 0

      if (resp.data.itens.length > 0) {
        this.obs.tasks_faixa_simples = resp.data.itens
        this.obs.length_tasks_faixa_simples = resp.data.count
      }
    } catch (ex) {
      toast.error("Erro ao Carregar Relatórios Solicitados, tente novamente mais tarde.", {});
    }
  }

  /**
 * Realiza o download de um relatório no formato especificado para a empresa informada.
 * 
 * A função envia uma requisição `POST` para o endpoint `/api/relatorio/task/download`,
 * passando o `id` do relatório e o `format` desejado (exemplo: `"xlsx"`).
 * O backend retorna o arquivo no formato binário (`blob`), que é convertido e baixado no
 * navegador com o nome `{cnpj}_relatorio.{format}`.
 * 
 * Durante o processo, a propriedade observável `loading` é usada para indicar visualmente
 * que o download está em andamento.
 * 
 * Em caso de sucesso:
 * - O arquivo é baixado automaticamente no formato solicitado.
 * 
 * Em caso de falha:
 * - Exibe a mensagem de erro contida no cabeçalho (`headers.msg`), se presente.
 * - Caso contrário, mostra uma mensagem genérica de erro.
 * 
 * @async
 * @param {string} id Identificador único do relatório a ser baixado.
 * @param {string} cnpj CNPJ da empresa que será usado no nome do arquivo.
 * @param {string} format Formato do arquivo a ser baixado (ex.: `"xlsx"`).
 * 
 * @returns {Promise<void>} Retorna uma Promise resolvida após a tentativa de download.
 */

  async handleDownloadArquivoRelatorio(id: string, cnpj: string) {
    this.obs.loading = true

    try {

      let resp = await api.post(`/api/relatorio/task/download-faixa-simples`, {id: id, format: "xlsx"}, { responseType: 'blob' });

      if(resp.status == 200){

        const fileName = cnpj + "_relatorio.xlsx";

        downloadBinaryFile(resp.data, fileName)

      } else {

        toast.error(decodeUtf8String(resp.headers.msg), {});

      }

    } catch (ex) {

      toast.error('erro ao baixar o arquivo, tente novamente mais tarde ou contate o suporte.', {});

    }

    this.obs.loading = false
  }
}

export const FaixaSimplesStore = new _FaixaSimplesStore();
